import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import api from "../utils/api";

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [costProjection, setCostProjection] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [vendorAnalysis, setVendorAnalysis] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [vatAnalytics, setVatAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1), // January 1st of current year
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // Default to next year
  });

  // Function to get the latest license expiry date from the database
  const getLatestLicenseExpiry = useCallback(async () => {
    try {
      const response = await api.get("/api/reports/latest-license-expiry");
      if (response.data && response.data.latestExpiry) {
        const latestExpiry = new Date(response.data.latestExpiry);
        setDateRange((prev) => ({
          ...prev,
          endDate: latestExpiry,
        }));
      }
    } catch (error) {
      console.warn(
        "Could not fetch latest license expiry, using default end date:",
        error,
      );
    }
  }, []);

  // Initialize dates on component mount
  useEffect(() => {
    getLatestLicenseExpiry();
  }, [getLatestLicenseExpiry]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);

      const [
        costResponse,
        breakdownResponse,
        vendorResponse,
        dashboardResponse,
      ] = await Promise.all([
        api.get("/api/reports/cost-projection", {
          params: {
            startDate: dateRange.startDate.toISOString().split("T")[0],
            endDate: dateRange.endDate.toISOString().split("T")[0],
          },
        }),
        api.get("/api/reports/category-breakdown"),
        api.get("/api/reports/vendor-analysis"),
        api.get("/api/reports/dashboard-summary"),
      ]);

      setCostProjection(costResponse.data);
      setCategoryBreakdown(breakdownResponse.data);
      setVendorAnalysis({
        ...vendorResponse.data,
        vendors: (vendorResponse.data.vendors || []).map((vendor) => ({
          ...vendor,
          total_cost: Number(vendor.total_cost) || 0,
          license_count: Number(vendor.license_count) || 0,
        })),
      });
      setDashboardSummary(dashboardResponse.data);
      setVatAnalytics(dashboardResponse.data.vatAnalytics);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      setError("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const COLORS = [
    "#2563EB",
    "#059669",
    "#DC2626",
    "#7C3AED",
    "#EA580C",
    "#0891B2",
  ];

  // Format currency values properly
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") {
      return "£0.00";
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return "£0.00";
    }
    return `£${numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Date Range Selector */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, startDate: date }))
                  }
                  textField={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, endDate: date }))
                  }
                  textField={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button variant="contained" onClick={fetchReports} fullWidth>
                  Update Reports
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Cost Projection Summary */}
        {costProjection && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Total number of active licenses across all categories and vendors"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Licenses
                    </Typography>
                    <Typography variant="h4">
                      {costProjection.summary.totalLicenses}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Total annual cost of all active licenses at their current billing rates (GBP)"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Current Cost (GBP)
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(
                        costProjection.summary.totalCurrentCostGbp,
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Estimated total cost for the next year, including renewals and new licenses (GBP)"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Projected Cost (GBP)
                    </Typography>
                    <Typography variant="h4" color="secondary">
                      {formatCurrency(
                        costProjection.summary.totalProjectedCostGbp,
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Monthly recurring cost across all active licenses (GBP)"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Monthly Cost (GBP)
                    </Typography>
                    <Typography variant="h4" color="warning">
                      {formatCurrency(
                        dashboardSummary?.monthlyRecurringCostGbp || 0,
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Yearly recurring cost across all active licenses (GBP)"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Yearly Cost (GBP)
                    </Typography>
                    <Typography variant="h4" color="info">
                      {formatCurrency(
                        dashboardSummary?.yearlyRecurringCostGbp || 0,
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
          </Grid>
        )}

        {/* VAT Analytics Summary */}
        {vatAnalytics && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Total VAT amount across all licenses that include VAT"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total VAT (GBP)
                    </Typography>
                    <Typography variant="h4" color="error">
                      {formatCurrency(vatAnalytics.total_vat_gbp)}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Total net amount (excluding VAT) across all licenses"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Net (GBP)
                    </Typography>
                    <Typography variant="h4" color="success">
                      {formatCurrency(vatAnalytics.total_net_gbp)}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Total amount including VAT for licenses that have VAT"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Inc. VAT (GBP)
                    </Typography>
                    <Typography variant="h4" color="info">
                      {formatCurrency(vatAnalytics.total_including_vat_gbp)}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip
                title="Number of licenses with VAT vs without VAT"
                placement="top"
                arrow
              >
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      VAT Split
                    </Typography>
                    <Typography variant="h6" color="textPrimary">
                      {vatAnalytics.licenses_with_vat} with VAT
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {vatAnalytics.licenses_without_vat} without VAT
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
          </Grid>
        )}

        {/* Cost Projection Chart */}
        {costProjection && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Projection by Category
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={costProjection.costProjection}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category_name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey="total_cost_gbp"
                    fill="#2563EB"
                    name="Current Cost (GBP)"
                  />
                  <Bar
                    dataKey="projectedCostGbp"
                    fill="#059669"
                    name="Projected Cost (GBP)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Category Breakdown */}
        {categoryBreakdown && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Licenses by Category
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={categoryBreakdown.breakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value, payload }) => {
                          const total = categoryBreakdown.breakdown.reduce(
                            (sum, item) => sum + item.total_licenses,
                            0,
                          );
                          const percentage =
                            total > 0 ? Math.round((value / total) * 100) : 0;
                          return percentage > 5 ? `${percentage}%` : "";
                        }}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="total_licenses"
                      >
                        {categoryBreakdown.breakdown.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => [`${value} licenses`, name]}
                        labelFormatter={(label) => `Category: ${label}`}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={24}
                        iconSize={12}
                        wrapperStyle={{ fontSize: "12px" }}
                        formatter={(value, entry) => [
                          `${value} (${entry.payload.total_licenses})`,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Category Cost Analysis
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryBreakdown.breakdown}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar
                        dataKey="total_active_cost"
                        fill="#2563EB"
                        name="Total Cost"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Vendor Analysis */}
        {vendorAnalysis && (
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vendor Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={vendorAnalysis.vendors}
                  margin={{ top: 8, right: 16, left: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="vendor"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId="cost"
                    width={72}
                    tickFormatter={(value) =>
                      `£${Number(value).toLocaleString("en-GB", {
                        maximumFractionDigits: 0,
                      })}`
                    }
                  />
                  <YAxis
                    yAxisId="count"
                    orientation="right"
                    allowDecimals={false}
                    width={40}
                  />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      if (name === "Total Cost") {
                        return [
                          `£${Number(value).toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`,
                          name,
                        ];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="cost"
                    dataKey="total_cost"
                    fill="#2563EB"
                    name="Total Cost"
                  />
                  <Bar
                    yAxisId="count"
                    dataKey="license_count"
                    fill="#059669"
                    name="License Count"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </Box>
  );
};

export default Reports;
